/**
 * The `app.pageload` anchor row — one per page load, emitted at bootstrap.
 *
 * It does two jobs, deliberately merged into a single row rather than the two
 * the original plan sketched (`app.pageload` + `app.environment`). One write per
 * page load, not two:
 *
 *  1. ANCHOR. It makes `journeyPageLoad` observable without needing any other
 *     event to have fired. `max(journeyPageLoad)` over a journey is the reload
 *     count — the tab-eviction signature CHANGELOG [2.4.16] had to spot by eye,
 *     and a struggle signal in its own right.
 *
 *  2. DEVICE CLASS. It carries the capability snapshot that separates "the
 *     instructions were unclear" from "this device could never have kept up".
 *     Those need opposite UX fixes and today nothing in `pwa_logs` tells them
 *     apart — the schema has browser and OS strings and nothing else.
 *
 * Everything here is read once, synchronously, from APIs that cannot block.
 * No feature detection that instantiates a WebGL context, no async probes: this
 * runs before first paint on the critical path.
 *
 * Payload contract: `docs/telemetry/EVENTS.md` → `app.pageload`.
 */

import { logEvent } from "@/Utils/logger";
import { Events } from "./events";
import { getJourneyPageLoad } from "./ids";

/**
 * `navigator.connection` is Chromium-only (absent on every iOS browser, since
 * they are all WebKit). Absence is itself informative — do not treat it as an
 * error, and do not default it to a value that looks like a measurement.
 */
function readConnection(): { effectiveType: string | null; saveData: boolean | null } {
  try {
    const c = (navigator as any).connection;
    if (!c) return { effectiveType: null, saveData: null };
    return {
      effectiveType: typeof c.effectiveType === "string" ? c.effectiveType : null,
      saveData: typeof c.saveData === "boolean" ? c.saveData : null,
    };
  } catch {
    return { effectiveType: null, saveData: null };
  }
}

/**
 * How the page was opened. `standalone` means installed-to-homescreen, which
 * changes camera/motion permission behaviour and is worth splitting cohorts on.
 */
function readDisplayMode(): string {
  try {
    if ((navigator as any).standalone === true) return "standalone-ios";
    if (
      typeof window.matchMedia === "function" &&
      window.matchMedia("(display-mode: standalone)").matches
    ) {
      return "standalone";
    }
  } catch {
    /* ignore */
  }
  return "browser";
}

/**
 * In-app WebViews (Instagram, Facebook, the Google app, Gmail) are a distinct
 * and much worse platform: they evict auth state, restrict storage, and are
 * where the Mode-G re-auth failures documented in HERE_BE_DRAGONS.md live.
 * Coarse UA sniffing is the only available signal.
 */
function readWebView(): string | null {
  try {
    const ua = navigator.userAgent;
    if (/FBAN|FBAV/.test(ua)) return "facebook";
    if (/Instagram/.test(ua)) return "instagram";
    if (/\bGSA\b/.test(ua)) return "google-app";
    if (/Line\//.test(ua)) return "line";
    if (/\bwv\b/.test(ua)) return "android-webview";
    return null;
  } catch {
    return null;
  }
}

/**
 * How the browser says it got here. `reload` vs `navigate` vs `back_forward`
 * distinguishes a deliberate refresh from an OS tab eviction, which
 * `journeyPageLoad` alone cannot.
 */
function readNavigationType(): string | null {
  try {
    const entries = performance.getEntriesByType("navigation");
    const nav = entries && entries.length ? (entries[0] as any) : null;
    return nav && typeof nav.type === "string" ? nav.type : null;
  } catch {
    return null;
  }
}

/**
 * Viewport size, or nulls.
 *
 * `window.innerWidth` can legitimately be 0 this early — before first layout, in
 * a zero-sized frame, in some headless contexts. Recording a 0 would poison
 * every cohort split that buckets by screen size, because 0 reads as a
 * measurement rather than as "unknown". Non-positive values therefore become
 * `null`, and `orientation` is only derived when both dimensions are real.
 * `documentElement.clientWidth` is preferred where available; it is laid out
 * slightly earlier than `innerWidth` on some engines.
 */
function readViewport(): {
  viewportW: number | null;
  viewportH: number | null;
  dpr: number | null;
  orientation: string | null;
} {
  const positive = (n: unknown): number | null =>
    typeof n === "number" && isFinite(n) && n > 0 ? n : null;

  let w: number | null = null;
  let h: number | null = null;
  let dpr: number | null = null;

  try {
    const el = document.documentElement;
    w = positive(el && el.clientWidth) || positive(window.innerWidth);
    h = positive(el && el.clientHeight) || positive(window.innerHeight);
    dpr = positive(window.devicePixelRatio);
  } catch {
    /* ignore */
  }

  return {
    viewportW: w,
    viewportH: h,
    dpr,
    orientation: w !== null && h !== null ? (w > h ? "landscape" : "portrait") : null,
  };
}

export function buildEnvironmentSnapshot(): Record<string, unknown> {
  const connection = readConnection();
  const viewport = readViewport();

  return {
    journeyPageLoad: getJourneyPageLoad(),
    navigationType: readNavigationType(),
    displayMode: readDisplayMode(),
    webView: readWebView(),

    viewportW: viewport.viewportW,
    viewportH: viewport.viewportH,
    dpr: viewport.dpr,
    orientation: viewport.orientation,

    // Chromium-only; null on iOS. `deviceMemory` is bucketed by the spec
    // (0.25/0.5/1/2/4/8) so it is low-cardinality and safe to read directly.
    deviceMemory:
      typeof (navigator as any).deviceMemory === "number"
        ? (navigator as any).deviceMemory
        : null,
    hardwareConcurrency:
      typeof navigator.hardwareConcurrency === "number"
        ? navigator.hardwareConcurrency
        : null,
    effectiveType: connection.effectiveType,
    saveData: connection.saveData,

    // The Safari trap: frameWorker.ts converts ImageData → JPEG via
    // OffscreenCanvas, which does not exist in Safari/iOS workers. See TRAPS.md.
    hasOffscreenCanvas: typeof OffscreenCanvas !== "undefined",
    hasDeviceMotion: typeof (window as any).DeviceMotionEvent !== "undefined",
    // Only present on iOS 13+; its absence is why Android never sees a motion prompt.
    needsMotionPermission:
      typeof (window as any).DeviceMotionEvent !== "undefined" &&
      typeof (window as any).DeviceMotionEvent.requestPermission === "function",

    prefersReducedMotion: (() => {
      try {
        return (
          typeof window.matchMedia === "function" &&
          window.matchMedia("(prefers-reduced-motion: reduce)").matches
        );
      } catch {
        return null;
      }
    })(),

    referrer: (() => {
      // Origin only — a full referrer URL can carry query-string PII.
      try {
        return document.referrer ? new URL(document.referrer).origin : null;
      } catch {
        return null;
      }
    })(),
  };
}

/**
 * Emit the anchor row. Call ONCE at bootstrap, before the app mounts.
 *
 * Fire-and-forget: `logEvent` already swallows its own failures, and telemetry
 * must never delay or break boot.
 */
export function logPageLoad(): void {
  try {
    logEvent(Events.APP_PAGELOAD, "[telemetry] page load", {
      data: buildEnvironmentSnapshot(),
    });
  } catch {
    /* telemetry must never break bootstrap */
  }
}
